// 사용자별 AI 사용량 상세 — 랭킹 표 옆에 붙는 비모달 우측 패널
import React, { useEffect, useState } from 'react';
import { fetchUsageUserDetail } from '../../api/adminUsage';
import type { UsageUser, UsageUserDetail } from '../../api/adminUsage';
import { StatCard, UsageDailyChart, UsageKindBreakdown } from './UsageCharts';
import {
  REQUEST_COUNT_HINT,
  REQUEST_COUNT_LABEL,
  TOKEN_COLOR,
  formatDateTime,
  formatRelativeTime,
  isSystemUser,
  userLabel,
} from './usageFormat';
import styles from './AiUsage.module.css';

interface UserDetailPanelProps {
  /**
   * 목록·검색에서 클릭한 행. 신원 표기와 lastUsedAt은 이 값을 쓴다 —
   * 상세 응답의 user.lastUsedAt은 항상 null이기 때문이다.
   */
  user: UsageUser;
  /** 검색으로 연 경우 수치가 전부 0이라 lastUsedAt도 믿을 수 없다 */
  hasListValues: boolean;
  from: string;
  to: string;
  onClose: () => void;
}

export default function UserDetailPanel({ user, hasListValues, from, to, onClose }: UserDetailPanelProps) {
  const [detail, setDetail] = useState<UsageUserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const userId = user.userId;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchUsageUserDetail(userId, from, to)
      .then((result) => {
        if (!cancelled) setDetail(result);
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setDetail(null);
          setError(err.message);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [userId, from, to]);

  const label = userLabel(user);

  return (
    <aside className={styles.panel} aria-label="사용자 상세">
      <header className={styles.panelHeader}>
        <div className={styles.panelTitleGroup}>
          <span className={styles.panelKicker}>사용자 상세</span>
          <h2 className={styles.panelTitle}>{label.idNo}</h2>
          <span className={styles.panelSubtitle}>
            {label.dept}
            {label.shortId && ` · ${label.shortId}`}
          </span>
          {isSystemUser(user) && (
            <span className={styles.systemTag}>사용자 특정에 실패한 호출의 합계</span>
          )}
        </div>
        <button type="button" className={styles.panelClose} onClick={onClose} aria-label="닫기">×</button>
      </header>

      <div className={styles.panelBody}>
        {loading && <p className={styles.panelStatus}>불러오는 중…</p>}
        {!loading && error && <p className={styles.panelError}>{error}</p>}

        {!loading && !error && detail && (
          <>
            <div className={styles.panelMeta}>
              <span>기간 {from} ~ {to}</span>
              {/* 상세 응답의 lastUsedAt은 항상 null이라 목록에서 받은 값을 쓴다 */}
              {hasListValues && (
                <span title={formatDateTime(user.lastUsedAt)}>
                  마지막 사용 {formatRelativeTime(user.lastUsedAt)}
                </span>
              )}
            </div>

            <div className={styles.panelStats}>
              <StatCard label="총 토큰" value={detail.user.totalTokens} />
              <StatCard label="입력" value={detail.user.promptTokens} accent={TOKEN_COLOR.prompt} />
              <StatCard label="출력" value={detail.user.completionTokens} accent={TOKEN_COLOR.completion} />
              <StatCard label={REQUEST_COUNT_LABEL} value={detail.user.requestCount} hint={REQUEST_COUNT_HINT} />
            </div>

            <section className={styles.panelSection}>
              <h3 className={styles.panelSectionTitle}>일별 추이</h3>
              <UsageDailyChart rows={detail.daily} />
            </section>

            <section className={styles.panelSection}>
              <h3 className={styles.panelSectionTitle}>호출 종류</h3>
              <UsageKindBreakdown models={detail.models} />
            </section>
          </>
        )}
      </div>
    </aside>
  );
}
